import { useEffect, useState } from "react";

import { Link, useNavigate, useSearchParams } from "@remix-run/react";
import { Loader2, Mail } from "lucide-react";

import Layout from "~/components/layout";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { H3, P } from "~/components/ui/typography";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/ssr.browser";
import { captureException, trackEvent } from "~/lib/analytics";
import { logger } from "~/lib/logger";
import type { AuthError } from "~/types";
type Step = "email" | "confirm-email" | "password" | "success";

const LinkAccount = () => {
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();
    const session = useSupabaseSession();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get("redirectTo") || "/";

    useEffect(() => {
        if (
            step === "password" &&
            !session.loading &&
            session?.user?.email &&
            session?.user?.id
        ) {
            (async () => {
                const userEmail = session.user?.email;
                const userId = session.user?.id;

                if (!userEmail || !userId) return;

                const { data, error, ...rest } = await supabase
                    .from("profiles")
                    .update({
                        email: userEmail,
                    })
                    .eq("id", userId)
                    .select("id, email");

                if (error) throw error;
            })();
        }
    }, [session.loading, session.user?.email, session.user?.id, step]);

    useEffect(() => {
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === "SIGNED_IN" && session?.user?.email_confirmed_at) {
                if (session?.user?.user_metadata.requiresPasswordChange) {
                    setStep("password");
                }
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            logger.debug("Updating user email with supabase auth");
            const { error } = await supabase.auth.updateUser(
                {
                    email,
                    data: {
                        requiresPasswordChange: true,
                    },
                },
                {
                    emailRedirectTo: `${window.location.origin}/auth/callback?next=/email-linking-success`,
                },
            );
            trackEvent("confirmation email sent", {
                email: email,
                source: "linking",
            });

            if (error) throw error;

            setStep("confirm-email");
            setLoading(false);

            toast({
                title: "Email updated",
                description:
                    "Please check your email for a confirmation link then return here.",
            });
        } catch (error) {
            const authError = error as AuthError;
            toast({
                title: "Error",
                description: authError.message,
                variant: "destructive",
            });
            captureException("Error updating user email", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({
                password,
                data: {
                    requiresPasswordChange: false,
                },
            });

            if (error) throw error;

            setStep("success");
            toast({
                title: "Success!",
                description: "Your account has been created.",
            });
            const eventEmail =
                session?.user?.email ||
                session?.user?.user_metadata?.email ||
                email;

            const eventProps = {
                source: "linking" as const,
                ...(eventEmail ? { email: eventEmail } : {}),
            };

            trackEvent("signed up", eventProps);
            setTimeout(() => navigate(redirectTo), 2000);
        } catch (error) {
            const authError = error as AuthError;
            toast({
                title: "Error",
                description: authError.message,
                variant: "destructive",
            });
            captureException("Error updating user email", error);
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case "email":
                return (
                    <form
                        onSubmit={handleEmailSubmit}
                        className="w-full space-y-4"
                    >
                        <Input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                "Continue"
                            )}
                        </Button>
                        <Button
                            asChild
                            variant="ghost"
                            className="w-full mt-8"
                            disabled={loading}
                        >
                            <Link to="/login">
                                Already have an account? Sign in
                            </Link>
                        </Button>
                    </form>
                );
            case "confirm-email":
                return (
                    <div className="flex flex-col items-center gap-4 text-center md:items-start">
                        <div className="flex size-16 items-center justify-center rounded-full bg-brand-forest-100">
                            <Mail className="size-8 text-brand-terra-600" />
                        </div>
                        <P>
                            We've sent a confirmation link to{" "}
                            <span className="font-medium">{email}</span>. Please
                            click the link to verify your email address.
                        </P>
                    </div>
                );
            case "password":
                return (
                    <form onSubmit={handlePasswordSubmit} className="space-y-4">
                        <Input
                            type="password"
                            placeholder="Create a password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                        />
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                "Create Account"
                            )}
                        </Button>
                    </form>
                );
            case "success":
                return (
                    <div className="text-center">
                        <P>Redirecting you...</P>
                    </div>
                );
        }
    };

    return (
        <Layout
            narrow={true}
            title={
                step === "success"
                    ? "Account created!"
                    : step === "confirm-email"
                      ? "Check your email"
                      : step === "password"
                        ? "Your email has been confirmed."
                        : "Create an account"
            }
            description={
                step === "email"
                    ? "Enter your email to get started"
                    : step === "password"
                      ? "Now, create a password for your account"
                      : step === "confirm-email"
                        ? ""
                        : "Your account has been created!"
            }
        >
            <div className="mx-auto flex w-full max-w-sm justify-center md:mx-0 md:w-full md:justify-start">
                {renderStep()}
            </div>
        </Layout>
    );
};

export default LinkAccount;
